const { getElements } = window.BAO.Utils

/**
 * @typedef {Object} SelectedVariantOption
 *
 * @property {string} label
 * @property {string} value
 */

const selectors = {
  addToCart: '[data-product-add-to-cart]',
  addToCartText: '[data-product-add-to-cart-text]',
  backInStockTrigger: '[data-product-bis-trigger]',
  form: '[data-product-form]',
  price: '[data-product-price]',
  sku: '[data-product-sku]',
  variantIndicator: '[data-product-variant-indicator]',
  variantContent: '[data-product-variant-content]',
  variantDrawerTrigger: '[data-product-variant-trigger]',
  wishlistTrigger: '[data-product-wishlist-trigger]',
}

class Variant {
  constructor (data) {
    /**
     * @type {ShopifyVariant}
     * @public
     */
    this.data = data
  }

  async setPrice (context) {
    await window.BAO.sectionRenderer
      .fetch(
        'product-price',
        `${context.dataset.url}?variant=${this.data.id}&section_id=product-price`,
      )
      .then((res) => {
        const newDOM = window.BAO.sectionRenderer.convertToDOM(
          res['product-price'],
        )
        const myself = newDOM.querySelector('.shopify-section')

        const priceElements = getElements(
          `${selectors.price}[data-form-id='${context.dataset.formId}']`,
        )
        priceElements.forEach((el) => {
          el.innerHTML = myself.innerHTML
        })
      })
  }

  setAvailability (context) {
    getElements(
      `${selectors.addToCart}[data-form-id='${context.dataset.formId}']`,
    ).forEach((el) => {
      el.disabled = !this.data.available
      el.setAttribute('aria-hidden', !this.data.available)
    })

    getElements(`${selectors.backInStockTrigger}`).forEach((el) => {
      el.setAttribute('aria-hidden', this.data.available)
      el.setAttribute(
        'data-product-url',
        `${this.url.toString()}?variant=${this.data.id}`,
      )
    })

    let addToCartText = window.theme.strings.addToCart
    const preorderData = JSON.parse(
      document
        .querySelector('product-preorder')
        ?.getAttribute('data-preorder-data') || '[]',
    )
    const variantIdString = this.data.id.toString()
    const variantData = preorderData.find(
      (item) => item.id === variantIdString,
    )
    const productTag = document.querySelector('.prd-Tags_Tag-preorder')
    const formFooter = document.querySelector('.prd-Form_Footer')
    const formFooterContainer = document.querySelector(
      '.prd-Form_FooterAncillaries',
    )
    const formFooterButtonEl = document.querySelector('.prd-Form_Button')
    const formFooterPreorderButtonIcon = document.querySelector(
      '.prd-Form_ButtonIcon-preorder',
    )
    const formFooterButtonIcon = document.querySelector(
      '.prd-Form_ButtonIcon-regular',
    )

    const formFixedBarAncillaries = document.querySelector(
      '.prd-FixedBar_Ancillaries',
    )
    const formFixedBarButtonEl = document.querySelector('.prd-FixedBar_Button')
    const formFixedBarPreorderButtonIcon = document.querySelector(
      '.prd-FixedBar_ButtonIcon-preorder',
    )
    const formFixedBarButtonIcon = document.querySelector(
      '.prd-FixedBar_ButtonIcon-regular',
    )

    if (variantData?.enable === 'true') {
      addToCartText = window.theme.strings.preOrder

      productTag?.setAttribute('aria-hidden', false)
      formFooter?.classList.add('prd-Form_Footer-preorder')
      formFooterButtonEl?.classList.add('prd-Form_Button-preorder')
      formFooterContainer?.setAttribute('aria-hidden', true)
      formFooterPreorderButtonIcon?.setAttribute('aria-hidden', false)
      formFooterButtonIcon?.setAttribute('aria-hidden', true)

      formFixedBarAncillaries?.setAttribute('aria-hidden', true)
      formFixedBarButtonEl.classList?.add('prd-FixedBar_Button-preorder')
      formFixedBarPreorderButtonIcon?.setAttribute('aria-hidden', false)
      formFixedBarButtonIcon?.setAttribute('aria-hidden', true)
    } else {
      const formFooterContainer = document.querySelector(
        '.prd-Form_FooterAncillaries',
      )

      productTag?.setAttribute('aria-hidden', true)
      formFooter?.classList.remove('prd-Form_Footer-preorder')
      formFooterButtonEl?.classList.remove('prd-Form_Button-preorder')
      formFooterContainer?.setAttribute('aria-hidden', false)
      formFooterPreorderButtonIcon?.setAttribute('aria-hidden', true)
      formFooterButtonIcon?.setAttribute('aria-hidden', false)

      formFixedBarAncillaries?.setAttribute('aria-hidden', false)
      formFixedBarButtonEl?.classList.remove('prd-FixedBar_Button-preorder')
      formFixedBarPreorderButtonIcon?.setAttribute('aria-hidden', true)
      formFixedBarButtonIcon?.setAttribute('aria-hidden', false)
    }

    getElements(
      `${selectors.addToCartText}[data-form-id='${context.dataset.formId}']`,
    ).forEach(
      (el) =>
        (el.textContent = this.data.available
          ? addToCartText
          : window.theme.strings.soldOut),
    )
  }

  updateInput (context) {
    const inputs = getElements(
      `form[action*="/cart/add"][id="${context.dataset.formId}"] [name="id"]`,
    )

    inputs.forEach((input) => {
      input.value = this.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  updateIndicator (name, value, context) {
    const variantIndicators = getElements(
      `${selectors.variantIndicator}[data-form-id='${context.dataset.formId}']`,
    )

    const variantIndicator = variantIndicators.find(
      (el) => el.dataset.name === name,
    )

    if (!variantIndicator) return

    variantIndicator.textContent = value
  }

  updatePackageContent (name, value, context) {
    const variantContents = getElements(
      `${selectors.variantContent}[data-form-id='${context.dataset.formId}']`,
    )

    const variantContent = variantContents.find(
      (el) => el.dataset.name === name,
    )

    if (!variantContent) return

    variantContent.textContent = value
  }

  updateWishlistTrigger () {
    const triggers = getElements(selectors.wishlistTrigger)
    let allHaveAttribute = true

    triggers.forEach((el) => {
      if (!el.hasAttribute('data-wishlist-active')) {
        allHaveAttribute = false
        return
      }

      el.setAttribute('data-variant-id', this.id)
    })

    if (!allHaveAttribute) {
      return
    }

    document.dispatchEvent(new CustomEvent('checkIsInWishlist'))
  }

  setUrl () {
    window.history.replaceState(
      { path: this.url.toString() },
      '',
      this.url.toString(),
    )
  }

  setShareUrl () {
    const shareButtons = getElements('share-button')
    if (shareButtons.length === 0) return

    shareButtons.forEach((shareButton) => {
      shareButton.updateUrl(this.url.toString())
    })
  }

  /**
   * @returns {number}
   */
  get id () {
    return this.data.id
  }

  /**
   * @returns {URL}
   */
  get url () {
    // eslint-disable-next-line compat/compat
    const url = new URL(window.location.href)
    url.searchParams.set('variant', this.id.toString())

    return url
  }
}

export class VariantOptions extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['data', 'option', 'option-choice']
  }

  constructor () {
    super()

    /**
     * @type {Variant[]}
     * @public
     */
    this.collectedVariants = [new Variant(this.defaultVariant)]
  }

  setupListeners () {
    super.setupListeners()

    this.onVariantChange = this.onVariantChange.bind(this)

    this.listeners.add(this, 'change', this.onVariantChange)
  }

  onVariantChange (e) {
    const variant = this.getVariant()

    variant.setAvailability(this)
    variant.setPrice(this)
    variant.updateInput(this)
    variant.updateIndicator(e.target.name, e.target.dataset.value, this)
    variant.updatePackageContent(
      e.target.name,
      e.target.dataset.packageContent,
      this,
    )
    variant.updateWishlistTrigger()

    window.BAO.dispatchEvent(window.BAO.EVENTS.PRODUCT.VARIANT_CHANGE, {
      variant,
    })

    if (!this.currentVariant) {
      this.resetPage(variant)

      return
    }

    variant.setUrl()
    variant.setShareUrl()

    const drawerButton = document.querySelector('[data-drawers-close]')

    if (!drawerButton) return

    drawerButton.click()
  }

  /**
   * @param {Variant} variant
   *
   * @returns {void}
   */
  resetPage (variant) {
    window.history.replaceState(
      { path: window.location.pathname },
      '',
      window.location.pathname,
    )
  }

  /**
   * @param {ShopifyVariant} data
   *
   * @returns {Variant}
   */
  createVariant (data) {
    const variant = new Variant(data)
    this.collectedVariants.push(variant)

    return variant
  }

  /**
   * @returns {Variant}
   */
  getVariant () {
    if (!this.currentVariant) {
      return this.collectedVariants[0]
    }

    const existingVariant = this.collectedVariants.find(
      (variant) => variant.id === this?.currentVariant?.id,
    )
    if (existingVariant) return existingVariant

    return this.createVariant(this.currentVariant)
  }

  /**
   * @returns {SelectedVariantOption[]}
   */
  get selectedOptions () {
    this.throwError(`selectedOptions needs to be implemented on this class`)

    return []
  }

  /**
   * @returns {ShopifyVariant}
   */
  get defaultVariant () {
    return this.data[0]
  }

  /**
   * @returns {ShopifyVariant|undefined}
   */
  get currentVariant () {
    return this.data.find((variant) => {
      return variant.options.every(
        (option, index) => this.selectedOptions[index].value === option,
      )
    })
  }

  /**
   * @returns {ShopifyVariant[]}
   */
  get data () {
    return JSON.parse(this.els.data.element.innerText)
  }
}

export class VariantRadios extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map((el) => {
      const { name } = el.dataset
      const choice = getElements(this.els['option-choice'].selector, {
        context: el,
      }).find((el) => el.checked).value

      return {
        label: name,
        value: choice,
      }
    })
  }
}

export class VariantSelects extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map((el) => {
      const { name } = el.dataset

      return {
        label: name,
        value: el.querySelector('select').value,
      }
    })
  }
}

if (!customElements.get('variant-radios')) {
  customElements.define('variant-radios', VariantRadios)
}

if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', VariantSelects)
}
